import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { getTopCountriesWithOtherByMetric } from './utils/supplyOverviewTransform';
import getCurrencySymbol from '~/utils/getCurrencySymbol';

const metricOptions = ['Revenue', 'Ad Requests', 'Impressions', 'Clicks'];
const DISPLAY_COLORS = ['#3579D6', '#42B8C2', '#F5A623', '#EF5A5A', '#8A9DB2'];

function formatMetricValue(value, metric) {
  const formattedValue = Number(value ?? 0).toLocaleString();
  return metric === 'Revenue' ? `${getCurrencySymbol()} ${formattedValue}` : formattedValue;
}

function getTopCountry(countries, metric) {
  if (!countries.length) {
    return null;
  }

  return countries.reduce((highest, item) => {
    const nextValue = Number(item.metrics?.[metric] ?? 0);
    const highestValue = Number(highest.metrics?.[metric] ?? 0);
    return nextValue > highestValue ? item : highest;
  });
}

function CountrySplit({ countries, isLoading, onSelectedCountryChange }) {
  const [selectedMetric, setSelectedMetric] = useState('Revenue');
  const [selectedCountryId, setSelectedCountryId] = useState('');

  const displayedCountries = useMemo(
    () => getTopCountriesWithOtherByMetric(countries, selectedMetric),
    [countries, selectedMetric]
  );

  const totals = useMemo(() => {
    const countriesWithMetric = displayedCountries.map(country => ({
      ...country,
      value: Number(country.metrics?.[selectedMetric] ?? 0),
    }));

    const totalValue = countriesWithMetric.reduce((acc, item) => acc + item.value, 0);

    return countriesWithMetric
      .map(item => ({
        ...item,
        percent: totalValue > 0 ? (item.value / totalValue) * 100 : 0,
      }))
      .sort((left, right) => right.percent - left.percent)
      .map((item, index) => ({
        ...item,
        color: DISPLAY_COLORS[index % DISPLAY_COLORS.length],
      }));
  }, [displayedCountries, selectedMetric]);

  const topCountry = totals[0] ?? null;
  const overallTotal = totals.reduce((sum, country) => sum + country.value, 0);

  const selectedCountry = totals.find(country => country.id === selectedCountryId) || topCountry;

  useEffect(() => {
    if (typeof onSelectedCountryChange === 'function') {
      onSelectedCountryChange(selectedCountry || null);
    }
  }, [onSelectedCountryChange, selectedCountry]);

  const handleMetricChange = event => {
    const nextMetric = event.target.value;
    const nextDisplayedCountries = getTopCountriesWithOtherByMetric(countries, nextMetric);
    const nextTopCountry = getTopCountry(nextDisplayedCountries, nextMetric);

    setSelectedMetric(nextMetric);
    setSelectedCountryId(nextTopCountry?.id || '');
  };

  if (isLoading) {
    return (
      <Paper
        elevation={0}
        className="sas-country-split-loading">
        <Skeleton
          variant="text"
          width={160}
          height={32}
        />
        <Skeleton
          variant="rounded"
          height={34}
          sx={{ my: 2 }}
        />
        <Skeleton
          variant="rounded"
          height={22}
        />
      </Paper>
    );
  }

  return (
    <Paper
      elevation={2}
      className="sas-country-split-card">
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        className="sas-country-split-toolbar">
        <Typography
          variant="subtitle1"
          className="sas-country-split-title">
          Country
        </Typography>

        <FormControl
          size="small"
          sx={{ minWidth: 180 }}>
          <InputLabel id="country-split-metrics-label">Metrics</InputLabel>
          <Select
            labelId="country-split-metrics-label"
            id="country-split-metrics"
            value={selectedMetric}
            label="Metrics"
            onChange={handleMetricChange}>
            {metricOptions.map(option => (
              <MenuItem
                key={option}
                value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      {totals.length === 0 ? (
        <Box className="sas-country-split-empty">
          <Typography
            variant="body2"
            className="sas-country-split-empty-title">
            No country data is available yet
          </Typography>
        </Box>
      ) : null}

      {totals.length > 0 ? (
        <>
          <Box className="sas-country-split-bar">
            {totals.map((country, index) => {
              const isActive = country.id === selectedCountry?.id;
              const percentText = `${country.percent.toFixed(0)}%`;
              const tooltip = (
                <Box sx={{ py: 0.5 }}>
                  <Typography variant="body2">
                    Overall: {formatMetricValue(overallTotal, selectedMetric)}
                  </Typography>
                  <Typography variant="body2">
                    {country.name}: {formatMetricValue(country.value, selectedMetric)}
                  </Typography>
                </Box>
              );

              return (
                <Tooltip
                  key={country.id}
                  title={tooltip}
                  placement="top"
                  arrow>
                  <Box
                    component="button"
                    type="button"
                    onClick={() => setSelectedCountryId(country.id)}
                    className="sas-country-split-segment"
                    sx={{
                      flex: country.percent || 1,
                      backgroundColor: country.color,
                      opacity: isActive ? 1 : 0.6,
                      ...(index > 0 && {
                        boxShadow: 'inset 1px 0 0 rgba(255,255,255,0.45)',
                      }),
                    }}
                    aria-label={`${country.name} ${country.percent.toFixed(1)} percent`}>
                    <Typography
                      variant="caption"
                      className="sas-country-split-segment-label"
                      sx={{ opacity: isActive ? 1 : 0.85 }}>
                      {percentText}
                    </Typography>
                  </Box>
                </Tooltip>
              );
            })}
          </Box>

          <Stack
            direction="row"
            spacing={1.5}
            useFlexGap
            sx={{ mb: 2 }}>
            {totals.map(country => {
              const isActive = country.id === selectedCountry?.id;

              return (
                <Box
                  key={country.id}
                  component="button"
                  type="button"
                  onClick={() => setSelectedCountryId(country.id)}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 1,
                    border: '1px solid',
                    borderColor: isActive ? 'primary.main' : 'divider',
                    borderRadius: 12,
                    px: 1,
                    py: 0.25,
                    background: 'transparent',
                    cursor: 'pointer',
                    opacity: isActive ? 1 : 0.45,
                  }}>
                  <Box
                    sx={{
                      width: 9,
                      height: 9,
                      borderRadius: '50%',
                      backgroundColor: country.color,
                    }}
                  />
                  <Typography
                    variant="caption"
                    sx={{ fontWeight: 600 }}>
                    {country.name}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary">
                    {country.percent.toFixed(0)}%
                  </Typography>
                </Box>
              );
            })}
          </Stack>
        </>
      ) : null}
    </Paper>
  );
}

export default CountrySplit;
